import { useState, useCallback } from 'react';
import type { OrganizationType } from '@signalvote/shared';
import { organizationTypeLabel } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import {
  useOrganizationTypeConflicts,
  useResolveOrganizationTypeConflict,
  useDismissOrganizationTypeConflict,
} from '@/hooks/queries/useOrganizationTypeConflicts';
import { CLIENT_CONFLICT_MARKER } from '@/lib/clientConflict';

// Issue #350 (Layer A) — the inline fix-it alert for a contact-merge OrganizationType conflict on
// the contact-detail page. #348 kept the surviving record's subtype over a conflicting merged value
// but never guessed which is right; because BOTH candidates were recorded, the fix is a single tap.
// Modeled on the duplicate-contact resolve surface (amber inline alert), but for a single surviving
// contact rather than a pair. Resolving/dismissing here also clears the filing preflight backstop
// (both read the same audit-derived source).

interface OrganizationTypeConflictAlertProps {
  contactId: string;
}

export function OrganizationTypeConflictAlert({ contactId }: OrganizationTypeConflictAlertProps) {
  // The list endpoint requires Finance/Manage; a non-Manage user's query would 403, so gate the
  // whole surface on the same capability the resolve/dismiss endpoints demand.
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const { data } = useOrganizationTypeConflicts();
  const { mutateAsync: resolve, isPending: resolving } = useResolveOrganizationTypeConflict();
  const { mutateAsync: dismiss, isPending: dismissing } = useDismissOrganizationTypeConflict();
  const [error, setError] = useState<string | null>(null);

  const conflict = data?.conflicts.find((c) => c.contactId === contactId);
  const busy = resolving || dismissing;

  const messageFor = (err: unknown) =>
    err instanceof Error && err.message === CLIENT_CONFLICT_MARKER
      ? 'This contact was just changed elsewhere. Refresh and try again.'
      : err instanceof Error
        ? err.message
        : 'Something went wrong.';

  const handleResolve = useCallback(
    async (organizationType: OrganizationType, concurrencyVersion: number) => {
      setError(null);
      try {
        await resolve({ contactId, organizationType, concurrencyVersion });
      } catch (err) {
        setError(messageFor(err));
      }
    },
    [contactId, resolve],
  );

  const handleDismiss = useCallback(
    async (concurrencyVersion: number) => {
      setError(null);
      try {
        await dismiss({ contactId, concurrencyVersion });
      } catch (err) {
        setError(messageFor(err));
      }
    },
    [contactId, dismiss],
  );

  if (!canManage || !conflict) return null;

  const keptLabel = organizationTypeLabel(conflict.keptOrganizationType);
  const discardedLabel = organizationTypeLabel(conflict.discardedOrganizationType);

  return (
    <div
      data-testid="org-type-conflict-alert"
      className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-4"
    >
      <p className="text-sm font-medium text-amber-800">Organization type conflict from a merge</p>
      <p className="text-sm text-amber-700">
        We kept <strong>{keptLabel}</strong>; the merged record said <strong>{discardedLabel}</strong>.
        Which is correct?
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => handleResolve(conflict.keptOrganizationType, conflict.concurrencyVersion)}
          className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          It's {keptLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => handleResolve(conflict.discardedOrganizationType, conflict.concurrencyVersion)}
          className="rounded-md border border-amber-400 bg-white px-3 py-1 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50"
        >
          It's {discardedLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => handleDismiss(conflict.concurrencyVersion)}
          className="px-3 py-1 text-sm text-muted-foreground underline hover:text-foreground disabled:opacity-50"
        >
          Dismiss
        </button>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  );
}
